import { Alert, Button, CardContent, Typography, Box } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { PaymentMethod } from "@appTypes/index";
import {
  isWalletType,
  getPaymentMethodTitle,
  getPaymentMethodSubtitle,
  isPaymentMethodExpired,
  getPaymentMethodIconType,
} from "@utils/getPaymentMethodDisplayInformation";
import PaymentMethodIcon from "@components/PaymentMethods/PaymentMethodIcon";

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethod;
  onMakePrimary: () => void;
  onDelete: () => void;
}

const PaymentMethodCard = ({ paymentMethod, onMakePrimary, onDelete }: PaymentMethodCardProps) => {
  const { t } = useTranslation();
  const isWallet = isWalletType(paymentMethod.type);
  const title = t(getPaymentMethodTitle(paymentMethod));
  const subtitle = getPaymentMethodSubtitle(paymentMethod);
  const translatedSubtitle = subtitle.startsWith("Expires ")
    ? `${t("Expires")} ${subtitle.slice("Expires ".length)}`
    : t(subtitle);
  const isExpired = isPaymentMethodExpired(paymentMethod);

  return (
    <CardContent className="p-4" data-testid="payment-method-card">
      <Box className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Box className="flex items-center gap-3 min-w-0">
          <PaymentMethodIcon type={getPaymentMethodIconType(paymentMethod)} />
          <Box className="min-w-0">
            <Typography variant="body2" color="text.primary">
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant={isWallet ? "caption" : "body2"}
                color={isWallet ? "text.secondary" : "text.primary"}
              >
                {translatedSubtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {paymentMethod.isDefault ? (
          <Box className="flex items-center gap-1 justify-end shrink-0">
            <Typography variant="body2" color="text.secondary">
              {t("Primary")}
            </Typography>
            <CheckCircle color="success" fontSize="small" />
          </Box>
        ) : (
          <Box className="flex items-center gap-2 justify-end shrink-0">
            <Button variant="text" color="error" size="small" onClick={onDelete}>
              {t("Delete")}
            </Button>
            <Button variant="text" color="primary" size="small" onClick={onMakePrimary}>
              {t("Make Primary")}
            </Button>
          </Box>
        )}
      </Box>
      {isExpired && (
        <Alert severity="error" className="mt-3">
          {t("Your card has expired. Please add a new card.")}
        </Alert>
      )}
    </CardContent>
  );
};

export default PaymentMethodCard;
